import { useState, useEffect } from 'react';
import { subscriptionManagementApi } from '../services/subscriptionManagementApi';

export const useAccountStatus = () => {
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAccountStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Vérifier d'abord si l'email est vérifié dans le localStorage
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        if (user.isEmailVerified === true) {
          // L'email est vérifié, pas besoin de charger l'état EMAIL_NOT_VERIFIED
          const response = await subscriptionManagementApi.getAccountStatus();
          
          if (response.success) {
            const accountInfo = response.data.account || response.data;
            setAccountData(accountInfo);
          } else {
            setError(response.error);
          }
          setLoading(false);
          return;
        }
      }
      
      // Sinon, charger normalement depuis l'API
      const response = await subscriptionManagementApi.getAccountStatus();
      
      if (response.success) {
        // Les données sont dans response.data.account
        const accountInfo = response.data.account || response.data;
        setAccountData(accountInfo);
      } else {
        // Gérer le cas spécifique de l'email non vérifié
        if (response.errorCode === 'EMAIL_NOT_VERIFIED') {
          setAccountData({
            status: 'email_not_verified',
            email: response.email,
            errorMessage: response.error
          });
        } else {
          setError(response.error);
        }
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Ne charger que si l'utilisateur est connecté
    const token = localStorage.getItem('token');
    if (token) {
      loadAccountStatus();
    } else {
      setLoading(false);
    }

    // Écouter les changements de localStorage pour recharger automatiquement
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'user') {
        const newToken = localStorage.getItem('token');
        if (newToken) {
          loadAccountStatus();
        } else {
          setAccountData(null);
          setLoading(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Écouter aussi les événements personnalisés pour les changements dans le même onglet
    const handleLocalUpdate = () => {
      const token = localStorage.getItem('token');
      if (token) {
        loadAccountStatus();
      }
    };
    
    window.addEventListener('localStorageUpdated', handleLocalUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdated', handleLocalUpdate);
    };
  }, []);

  // Vérifier s'il y a des problèmes avec le compte
  const hasIssues = accountData && (
    accountData.status === 'suspended' ||
    accountData.status === 'in_grace_period' ||
    accountData.status === 'email_not_verified' ||
    accountData.failed_payments > 0
  );

  // Déterminer la sévérité du problème
  const issueSeverity = () => {
    if (!accountData) return null;
    
    if (accountData.status === 'email_not_verified') return 'warning';
    if (accountData.status === 'suspended') return 'critical';
    if (accountData.status === 'in_grace_period') return 'warning';
    if (accountData.failed_payments > 2) return 'warning';
    if (accountData.failed_payments > 0) return 'info';
    
    return null;
  };

  // Obtenir le message d'alerte approprié
  const getAlertMessage = () => {
    if (!accountData) return null;

    switch (accountData.status) {
      case 'email_not_verified':
        return {
          type: 'warning',
          title: 'Vérification email requise',
          message: 'Vous devez vérifier votre adresse email pour accéder à toutes les fonctionnalités.',
          action: 'verify_email',
          email: accountData.email
        };
      
      case 'suspended':
        return {
          type: 'error',
          title: 'Compte suspendu',
          message: 'Votre compte est suspendu. Veuillez mettre à jour votre mode de paiement.',
          action: 'reactivate'
        };
      
      case 'grace_period':
        // Vérifier si la période de grâce est expirée
        const isExpired = !accountData.is_in_grace_period;
        const graceEndDate = new Date(accountData.grace_period_end_date);
        
        if (isExpired) {
          return {
            type: 'error',
            title: 'Période de grâce expirée',
            message: `Votre période de grâce a expiré le ${graceEndDate.toLocaleDateString('fr-FR')}. Mettez à jour votre mode de paiement pour éviter la suspension.`,
            action: 'update_payment'
          };
        } else {
          const daysLeft = Math.ceil((graceEndDate - new Date()) / (1000 * 60 * 60 * 24));
          return {
            type: 'warning',
            title: 'Période de grâce',
            message: `Votre compte sera suspendu dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}.`,
            action: 'update_payment'
          };
        }
      
      case 'cancelled':
        return {
          type: 'info',
          title: 'Abonnement annulé',
          message: 'Votre abonnement a été annulé.',
          action: null
        };
      
      default:
        if (accountData.failed_payments > 0) {
          return {
            type: 'warning',
            title: 'Problème de paiement',
            message: `${accountData.failed_payments} échec${accountData.failed_payments > 1 ? 's' : ''} de paiement détecté${accountData.failed_payments > 1 ? 's' : ''}.`,
            action: 'update_payment'
          };
        }
        return null;
    }
  };

  // Vérifier si le compte est actif
  const isActive = accountData && accountData.status === 'active';

  // Vérifier si le compte peut être réactivé
  const canReactivate = accountData && accountData.status === 'suspended';

  // Vérifier si l'abonnement peut être annulé
  const canCancel = accountData && ['active', 'in_grace_period'].includes(accountData.status);

  return {
    accountData,
    loading,
    error,
    hasIssues,
    issueSeverity: issueSeverity(),
    alertMessage: getAlertMessage(),
    isActive,
    canReactivate,
    canCancel,
    refresh: loadAccountStatus
  };
};